import React, { useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import Loading from "../loading/Loading";
import "./DigitalRecognitionProject.css";

const DigitalRecognitionProject = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleTryNow = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Add smooth transition effect
    const button = document.querySelector(".project-link") as HTMLElement;
    if (button) {
      button.style.transform = "scale(0.95)";
      setTimeout(() => {
        button.style.transform = "scale(1)";
      }, 150);
    }

    // Show loading and navigate
    setIsLoading(true);
  };

  const handleLoadingComplete = () => {
    navigate("/digitalrecongnization");
    setIsLoading(false);
  };

  return (
    <>
      {isLoading && (
        <Loading
          duration={1500}
          onComplete={handleLoadingComplete}
          text="🎨 Loading AI Magic..."
          theme="gold"
        />
      )}
      <div className="digital-recognition-project" id="featured-projects">
        <div className="project-header">
          <motion.div
            className="project-icon"
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ duration: 0.3 }}
          >
            ✍️
          </motion.div>
          <h2 className="project-title">Featured Project</h2>
        </div>

        <motion.div
          className="project-card"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="project-content">
            <div className="project-info">
              <h3 className="project-name">Handwritten Digit Recognition</h3>
              <p className="project-description">
                An interactive AI-powered web application that recognizes
                handwritten digits (0-9) using a custom-trained CNN model. I
                developed and trained the neural network from scratch using
                PyTorch, achieving 99.2% accuracy on the MNIST dataset. Draw on
                the canvas and watch as my trained model predicts your digit
                with confidence scores and probability distributions.
              </p>

              <div className="project-features">
                <div className="feature-item">
                  <span className="feature-icon">🎨</span>
                  <span>Interactive Drawing Canvas</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🧠</span>
                  <span>CNN Neural Network</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📊</span>
                  <span>Real-time Predictions</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📱</span>
                  <span>Touch & Mouse Support</span>
                </div>
              </div>

              <div className="project-tech">
                <span className="tech-tag">React</span>
                <span className="tech-tag">TypeScript</span>
                <span className="tech-tag">Python</span>
                <span className="tech-tag">PyTorch</span>
                <span className="tech-tag">Flask</span>
                <span className="tech-tag">CNN</span>
              </div>

              <div className="project-links">
                <a
                  href="https://github.com/jameszhan02/Kaggle_Competitions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="model-link"
                >
                  <span className="model-icon">🧠</span>
                  <span>View Model Training Code</span>
                </a>

                <motion.div
                  className="project-cta"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <button className="project-link" onClick={handleTryNow}>
                    <span>Dive In</span>
                    <motion.span
                      className="arrow"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      →
                    </motion.span>
                  </button>
                </motion.div>
              </div>
            </div>

            <div className="project-preview">
              <motion.div
                className="preview-canvas"
                animate={{
                  boxShadow: [
                    "0 0 20px rgba(255, 193, 7, 0.3)",
                    "0 0 30px rgba(255, 193, 7, 0.5)",
                    "0 0 20px rgba(255, 193, 7, 0.3)",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <div className="canvas-mockup">
                  <div className="canvas-border"></div>
                  <div className="drawing-demo">
                    <motion.div
                      className="demo-digit"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      7
                    </motion.div>
                  </div>
                </div>
              </motion.div>

              <div className="preview-stats">
                <div className="stat-item">
                  <span className="stat-number">99.2%</span>
                  <span className="stat-label">Accuracy</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">0.3s</span>
                  <span className="stat-label">Response</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">10</span>
                  <span className="stat-label">Digits</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default DigitalRecognitionProject;
